import { html } from 'lit-element';

import { SimpleLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import { selectors as cartSelectors } from '@elab/core/store/cart';
import { selectors as uiSelectors } from '@elab/core/store/ui';
import {
    addListener,
    createPropertiesGroup,
    deferred,
    setGroupDefaultValues,
} from '@elab/core/utils/webComponent';
import Breakpoints from '@elab/core/utils/Breakpoints';

/**
 * Component is used to display applied gift cards carousel
 *
 * @attr {String} [card-number-label = ''] - card number label
 * @attr {String} [expire-on-label = ''] - expire on date label
 * @attr {String} [amount-use-label = ''] - amount to use label
 * @attr {String} [remove-dialog-name = ''] - remove confirmation dialog name
 * @attr {Object} [accordionExpanded] - flag to check if parent accordion is expanded
 *
 * @prop {Array} [giftCards = []] - list of gift carts (from cart api data)
 *
 * HTML Structure Example
 *
 * <e2-gift-cards-carousel>
 * </e2-gift-cards-carousel>
 */

const sizesGroup = ['xxl', 'xl', 'lg', 'md', 'sm'];

export class GiftCardsCarousel extends SimpleLitElement {
    static get properties() {
        return {
            cardNumberLabel: {
                type: String,
                attribute: 'card-number-label',
            },
            expireOnLabel: {
                type: String,
                attribute: 'expire-on-label',
            },
            amountUseLabel: {
                type: String,
                attribute: 'amount-use-label',
            },
            removeDialogName: {
                type: String,
                attribute: 'remove-dialog-name',
            },
            removeAction: { type: String, attribute: 'remove-action' },
            dateFormat: { type: String, attribute: 'date-format' },
            ...createPropertiesGroup(Boolean, 'show-buttons', sizesGroup),
            ...createPropertiesGroup(Boolean, 'show-arrows', sizesGroup),
            giftCards: { type: Array, attribute: false },
            inputErrorMessage: {
                type: String,
                attribute: 'input-error-message',
            },
            illicadoRefundMessage: { type: String, attribute: 'illicado-refund-message' },
            windowInnerWidth: { type: Number, attribute: false },
            accordionExpanded: { type: Object, attribute: false },
        };
    }

    constructor() {
        super();

        setGroupDefaultValues(this, sizesGroup, 'showButtons', false);
        setGroupDefaultValues(this, sizesGroup, 'showArrows', false);

        this.cardNumberLabel = '';
        this.expireOnLabel = '';
        this.amountUseLabel = '';
        this.dateFormat = 'DD/MM/YY';
        this.giftCards = [];
        this.inputErrorMessage = '';
        this.activeCardType = '';
        this.illicadoRefundMessage = '';
        this.accordionExpanded = {};
        this.windowInnerWidth = window.innerWidth;
    }

    get carouselElement() {
        return this.querySelector('.cards-carousel');
    }

    updated(changedProps) {
        // this is because we cannot render when the accordion is closed.
        if (changedProps.has('accordionExpanded')) {
            // this is because on the store, the gift-accordion is this 'type' {"": boolean}
            if (Object.values(this.accordionExpanded).some(e => e)) {
                this.carouselElement.checkDevice();
            }
        }

        if (changedProps.has('giftCards') && this.carouselElement.getSwiperWrapper()) {
            setTimeout(this.carouselElement.setElements, 0);
            this.dispatchEvent(new CustomEvent('adjust-content-data'));
        }
    }

    onAccordionChange = deferred(() => {
        this.carouselElement.checkDevice();
        this.dispatchEvent(new CustomEvent('adjust-content-data'));
    }, 200);

    setActiveCardType(type) {
        this.activeCardType = type;
    }

    firstUpdated() {
        this.__accordionListener = addListener(
            document,
            'gift-accordion-CLOSE_OTHERS',
            this.onAccordionChange,
        );

        const observer = new MutationObserver(mutationsList => {
            for (const { target } of mutationsList) {
                if (target.classList.contains('swiper-slide-active')) {
                    this.setActiveCardType(target.cardType);
                    super.performUpdate();
                }
            }
        });

        observer.observe(this, {
            attributes: true,
            childList: true,
            subtree: true,
            attributeFilter: ['class'],
        });
    }

    disconnectedCallback() {
        this.__accordionListener && this.__accordionListener();
        super.disconnectedCallback();
    }

    giftCardListItem = card => html`
        <!-- class="swiper-slide" is required on top level to ensure swiper renders the component properly -->
        <e2-gift-card
            class="swiper-slide"
            card-number="${card.giftCard.cardNumber}"
            card-type="${card.giftCard.type}"
            card-label="${this.cardNumberLabel}"
            expire-label="${this.expireOnLabel}"
            amount-label="${this.amountUseLabel}"
            remove-dialog-name="${this.removeDialogName}"
            remove-action="${this.removeAction}"
            input-error-message="${this.inputErrorMessage}"
        ></e2-gift-card>
    `;

    renderIllicadoRefundInformation() {
        return html`
            <div class="global-message global-message--info">
                <i class="global-message__icon"></i>
                <span class="global-message__title">
                    ${this.illicadoRefundMessage}
                </span>
            </div>
        `;
    }

    render() {
        const isMobile = Breakpoints.isMobile(this.windowInnerWidth);

        return html`
            <e2-carousel
                clone-nodes
                class="cards-carousel"
                type="gift-card"
                ?show-arrows-xxl=${this.showArrowsXxl}
                ?show-arrows-xl=${this.showArrowsXl}
                ?show-arrows-lg=${this.showArrowsLg}
                ?show-arrows-md=${this.showArrowsMd}
                ?show-arrows-sm=${this.showArrowsSm}
                ?show-buttons-xxl=${this.showButtonsXxl}
                ?show-buttons-xl=${this.showButtonsXl}
                ?show-buttons-lg=${this.showButtonsLg}
                ?show-buttons-md=${this.showButtonsMd}
                ?show-buttons-sm=${this.showButtonsSm}
                ?no-touch-simulation=${!isMobile}
            >
                <div class="initial-items">
                    ${this.giftCards.map(this.giftCardListItem)}
                </div>
            </e2-carousel>

            ${this.activeCardType.toLowerCase() === 'illicado'
                ? this.renderIllicadoRefundInformation()
                : null}
        `;
    }
}

const mapStateToProps = state => ({
    giftCards: cartSelectors.getGiftCardTransactions(state),
    windowInnerWidth: uiSelectors.getWindowSize(state).width,
    accordionExpanded: uiSelectors.getExpandedAccordionGroup(state, 'gift-accordion'),
});

export default () =>
    customElements.define('e2-gift-cards-carousel', connect(mapStateToProps)(GiftCardsCarousel));
